import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DropdownContainer,
  DropdownInputContainerMulti,
  DropdownInputMulti,
  ChipsCounter,
  ChipsCounterContainer,
  DropdownInputWraper,
  DropdownWrapperContainer,
  DropdownIconContainer
} from '../styled-components'
import { Cancel } from '../../../atoms/icons/essentials'
import {
  TrailingDropDown,
  TrailingDropUp
} from '../../../atoms/icons/text-input'
import { dropdownBoxStyleOptions, dropdownHeaderOptions } from '../constants'
import { isEmpty } from 'lodash'
import ChipsComponent from '../../../atoms/chips'
import {
  chipsComponentTypeOptions,
  chipsSizeOptions,
  chipsVariantOptions
} from '../../../atoms/chips/constants'
import PropTypes from 'prop-types'
import DropdownHint from './dropdown-hint'
import DropdownClear from './dropdown-clear'
import DropdownLabel from './dropdown-label'
import ContextMenuComponent from '../../../organisms/context-menu'
import { contextMenuSizeOptions } from '../../../organisms/context-menu/constants'
import MenuHeader from './menu/menu-header'
import { iconSizeOptions } from '../../../atoms/icons/icons-sizes'

const DropdownMulti = ({
  boxStyle = dropdownBoxStyleOptions.MINIMAL,
  clearable,
  label,
  hint,
  disabled,
  readOnly,
  suggestions = [],
  placeholder = 'Placeholder',
  customOnChange,
  customOnChangeMulti,
  control = false,
  defaultValue = [],
  closeWhenSelected = false,

  radioProps = null,
  checkBoxProps = null,
  isLoading = false,

  options,
  menuHeader = null,
  menuProps = null,
  ...props
}) => {
  const safeMenuProps = menuProps || {}

  const [isOpen, setIsOpen] = useState(false)
  const [selectedItems, setSelectedItems] = useState(defaultValue)

  const inputRef = useRef()
  const wrapperRef = useRef()

  // tab states (outside of the component to avoid re-renders)
  const defaultTab = () => {
    if (menuHeader?.type === dropdownHeaderOptions.TABS) {
      return menuHeader?.tabOptions?.[0] ?? null
    }
    return null
  }

  const [selectedTab, setSelectedTab] = useState(defaultTab)

  const handleSelectTab = useCallback((event, tab) => {
    event.stopPropagation()
    setSelectedTab(tab)
  }, [])

  const handleClose = () => {
    setIsOpen(false)
  }

  const handleClickClear = (event) => {
    event.stopPropagation()
    setSelectedItems([])
  }

  const handleSelectItem = (items) => {
    setSelectedItems(items)

    if (closeWhenSelected) setIsOpen(false)
  }

  const handleRemoveItem = useCallback((event, id) => {
    event.stopPropagation()
    setSelectedItems((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const Arrow = () => {
    if (isOpen) {
      return <TrailingDropUp size={iconSizeOptions['ICON-S']} />
    }

    return <TrailingDropDown size={iconSizeOptions['ICON-S']} />
  }

  useEffect(() => {
    const handleEsc = (event) => event.key === 'Escape' && handleClose()
    const handleClick = (event) =>
      !wrapperRef.current?.contains(event.target) && handleClose()

    document.addEventListener('keydown', handleEsc)
    document.addEventListener('mousedown', handleClick)

    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [])

  useEffect(() => {
    if (!isEmpty(selectedItems) && customOnChangeMulti) {
      customOnChangeMulti(selectedItems)
    }
  }, [selectedItems])

  const showLabel = isOpen || !isEmpty(selectedItems)
  const notEmptySelectedItems = !isEmpty(selectedItems)
  const labelText = props.required ? `${label} *` : label
  const getPlaceholder = notEmptySelectedItems
    ? null
    : showLabel
      ? placeholder
      : labelText

  return (
    <DropdownWrapperContainer ref={wrapperRef}>
      <ContextMenuComponent.Root
        control={safeMenuProps.control}
        size={contextMenuSizeOptions.DESKTOP}
        isLoading={isLoading}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        style={{ width: 'auto' }}
        handleSelect={handleSelectItem}
        selectedItemsProp={selectedItems}
        setSelectedItemsProp={setSelectedItems}
      >
        <DropdownContainer>
          <DropdownLabel
            label={label}
            labelText={labelText}
            showLabel={showLabel}
            disabled={disabled}
            readOnly={readOnly}
          />

          <ContextMenuComponent.Trigger>
            <DropdownInputContainerMulti
              data-disabled={disabled}
              data-read-only={readOnly}
              boxStyle={boxStyle}
              notEmptySelectedItems={notEmptySelectedItems}
            >
              <DropdownInputWraper>
                {selectedItems.map((item) => (
                  <ChipsComponent.Root
                    key={item.id}
                    variant={chipsVariantOptions.MINIMAL}
                    size={chipsSizeOptions.SMALL}
                    componentType={chipsComponentTypeOptions.INPUT}
                  >
                    <ChipsComponent.Content>
                      {item?.title}
                    </ChipsComponent.Content>

                    <ChipsComponent.Action>
                      <ChipsComponent.Icon>
                        <Cancel
                          size={iconSizeOptions['ICON-XS']}
                          onClick={(event) => handleRemoveItem(event, item.id)}
                        />
                      </ChipsComponent.Icon>
                    </ChipsComponent.Action>
                  </ChipsComponent.Root>
                ))}
                <DropdownInputMulti
                  aria-haspopup="listbox"
                  aria-expanded={isOpen}
                  placeholder={getPlaceholder}
                  ref={inputRef}
                  spellCheck="false"
                  type="text"
                  disabled={disabled}
                  readOnly={true}
                  {...props}
                />
              </DropdownInputWraper>
              <DropdownIconContainer>
                {notEmptySelectedItems && (
                  <>
                    <ChipsCounterContainer>
                      <ChipsCounter>{selectedItems.length}</ChipsCounter>
                    </ChipsCounterContainer>

                    <DropdownClear
                      clearable={clearable}
                      handleClickClear={handleClickClear}
                      disabled={disabled}
                      readOnly={readOnly}
                    />
                  </>
                )}
                <DropdownIconContainer>
                  <Arrow />
                </DropdownIconContainer>
              </DropdownIconContainer>
            </DropdownInputContainerMulti>
            <ContextMenuComponent.List
              align={safeMenuProps.align}
              side={safeMenuProps.side}
              sideXOffset={safeMenuProps.sideXOffset}
              sideYOffset={safeMenuProps.sideYOffset}
              arrow={false}
              width={safeMenuProps.width}
            >
              <MenuHeader
                selectedTab={selectedTab}
                menuHeader={menuHeader}
                options={options}
                handleSelectTab={handleSelectTab}
              />
            </ContextMenuComponent.List>
          </ContextMenuComponent.Trigger>

          <DropdownHint hint={hint} />
        </DropdownContainer>
      </ContextMenuComponent.Root>
    </DropdownWrapperContainer>
  )
}

DropdownMulti.propTypes = {
  options: PropTypes.array.isRequired,
  children: PropTypes.node,
  control: PropTypes.bool,
  radioProps: PropTypes.any,
  checkBoxProps: PropTypes.any,
  isLoading: PropTypes.bool,

  boxStyle: PropTypes.oneOf(Object.values(dropdownBoxStyleOptions)),
  clearable: PropTypes.bool,
  label: PropTypes.string,
  hint: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  customOnChange: PropTypes.func,
  customOnChangeMulti: PropTypes.func,
  menuProps: PropTypes.shape({
    title: PropTypes.string,
    control: PropTypes.bool,
    align: PropTypes.string,
    side: PropTypes.string,
    sideXOffset: PropTypes.number,
    sideYOffset: PropTypes.number,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  menuHeader: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(dropdownHeaderOptions)).isRequired,
    title: PropTypes.string,
    placeholder: PropTypes.string,
    tabOptions: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        options: PropTypes.array.isRequired
      })
    )
  })
}

export default DropdownMulti
